/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import { AgentUsage, AgentUsage$inboundSchema } from "./agentusage.js";
import {
  ChatContentPart,
  ChatContentPart$inboundSchema,
} from "./chatcontentpart.js";
import { ChatMessage, ChatMessage$inboundSchema } from "./chatmessage.js";
import {
  ChatParticipant,
  ChatParticipant$inboundSchema,
} from "./chatparticipant.js";
import { ChatTotals, ChatTotals$inboundSchema } from "./chattotals.js";
import { RiskSegment, RiskSegment$inboundSchema } from "./risksegment.js";

export type Chat = {
  /**
   * Email of the AI account that produced the chat, resolved from the linked AI account. May differ from the employee's work email (e.g. a personal account).
   */
  accountEmail?: string | undefined;
  /**
   * Account type that produced the chat ('team', 'personal', or empty), resolved from the linked AI account.
   */
  accountType?: string | undefined;
  /**
   * The ID of the agent that produced this session, recorded from the authenticated actor at capture. Set for sessions an agent drove directly, such as a channel session, which has no assistant record.
   */
  agentId?: string | undefined;
  /**
   * The current name of the agent that produced this session. Absent when the agent has since been deleted, in which case agent_id is still reported.
   */
  agentName?: string | undefined;
  agentUsage?: AgentUsage | undefined;
  /**
   * The ID of the agent the assistant acts as, when the assistant has a dedicated agent identity
   */
  assistantAgentId?: string | undefined;
  /**
   * The ID of the assistant that produced this chat, if any
   */
  assistantId?: string | undefined;
  /**
   * The name of the assistant that produced this chat, if any
   */
  assistantName?: string | undefined;
  /**
   * Non-turn content attached to this chat, such as prompt attachments.
   */
  contentParts: Array<ChatContentPart>;
  /**
   * When the chat was created.
   */
  createdAt: Date;
  /**
   * The ID of the external user who created the chat
   */
  externalUserId?: string | undefined;
  /**
   * The generation that this response's messages belong to. A generation is an immutable snapshot of the transcript; a new one is opened on compaction or message edits, while normal turns append to the current one.
   */
  generation: number;
  /**
   * Whether newer messages exist after the last message in this page (within the returned generation). Load them with an `after_seq` cursor.
   */
  hasMoreAfter: boolean;
  /**
   * Whether older messages exist before the first message in this page (within the returned generation). Load them with a `before_seq` cursor.
   */
  hasMoreBefore: boolean;
  /**
   * The ID of the chat
   */
  id: string;
  /**
   * When the last message in the chat was created.
   */
  lastMessageTimestamp: Date;
  /**
   * True when the session's traffic was observed by the LiteLLM proxy, including sessions whose transcript is owned by the agent's own hook stream
   */
  litellmProxied?: boolean | undefined;
  /**
   * Present only when `query` was requested: contiguous runs of returned messages, each spanning one or more query matches and their surrounding context. Use each segment's cursors to expand it.
   */
  matchSegments?: Array<RiskSegment> | undefined;
  /**
   * The highest generation number present for this chat. To load the full history, walk from `max_generation` down to 0, requesting each generation in turn.
   */
  maxGeneration: number;
  /**
   * The list of messages in the chat for the returned generation, ordered oldest to newest by `seq`.
   */
  messages: Array<ChatMessage>;
  /**
   * The number of messages in the chat
   */
  numMessages: number;
  /**
   * The supported client that originated a chat routed through the source, when known
   */
  originatingClient?: string | undefined;
  /**
   * Distinct observed conversation participants across the session.
   */
  participants?: Array<ChatParticipant> | undefined;
  /**
   * True when the chat is pinned
   */
  pinned?: boolean | undefined;
  /**
   * Number of risk findings recorded against messages in this chat (project-scoped, found=true). Only populated by endpoints that join risk data; absent elsewhere.
   */
  riskFindingsCount?: number | undefined;
  /**
   * Present only when `risk_only` was requested: contiguous runs of returned messages, each spanning a risk finding and its surrounding context. Use each segment's cursors to expand it.
   */
  riskSegments?: Array<RiskSegment> | undefined;
  /**
   * Observed Slack channel associated with this session.
   */
  slackChannelId?: string | undefined;
  /**
   * Observed Slack channel name as reported by the captured envelope.
   */
  slackChannelName?: string | undefined;
  /**
   * Observed Slack workspace associated with this session.
   */
  slackTeamId?: string | undefined;
  /**
   * The source of the chat: Elements, Playground, ClaudeCode (inferred from messages)
   */
  source?: string | undefined;
  /**
   * Persisted LLM summary of the session transcript, if one has been generated
   */
  summary?: string | undefined;
  /**
   * When the session summary was last generated.
   */
  summaryGeneratedAt?: Date | undefined;
  /**
   * The title of the chat
   */
  title: string;
  /**
   * Total cost in USD for this chat
   */
  totalCost?: number | undefined;
  /**
   * Total input tokens used in this chat
   */
  totalInputTokens?: number | undefined;
  /**
   * Total output tokens used in this chat
   */
  totalOutputTokens?: number | undefined;
  /**
   * Total tokens (input + output) used in this chat
   */
  totalTokens?: number | undefined;
  /**
   * Trace-entry counts across the entire returned generation, independent of pagination. Each message maps to exactly one entry: a message carrying tool calls counts as a tool call regardless of role, otherwise the role decides.
   */
  totals?: ChatTotals | undefined;
  /**
   * When the chat was last updated.
   */
  updatedAt: Date;
  /**
   * The ID of the user who created the chat
   */
  userId?: string | undefined;
  /**
   * Work units of value delivered in this chat as judged by the work-units analysis. Absent unless the organization has work-units analysis enabled and this chat has been scored.
   */
  workUnits?: number | undefined;
  /**
   * Full work-units analysis verdict as JSON (per-task breakdown, rationales, and flags). Present only when `work_units` is present.
   */
  workUnitsReport?: string | undefined;
};

/** @internal */
export const Chat$inboundSchema: z.ZodMiniType<Chat, unknown> = z.pipe(
  z.object({
    account_email: z.optional(z.string()),
    account_type: z.optional(z.string()),
    agent_id: z.optional(z.string()),
    agent_name: z.optional(z.string()),
    agent_usage: z.optional(AgentUsage$inboundSchema),
    assistant_agent_id: z.optional(z.string()),
    assistant_id: z.optional(z.string()),
    assistant_name: z.optional(z.string()),
    content_parts: z.array(ChatContentPart$inboundSchema),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    external_user_id: z.optional(z.string()),
    generation: z.int(),
    has_more_after: z.boolean(),
    has_more_before: z.boolean(),
    id: z.string(),
    last_message_timestamp: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    litellm_proxied: z.optional(z.boolean()),
    match_segments: z.optional(z.array(RiskSegment$inboundSchema)),
    max_generation: z.int(),
    messages: z.array(ChatMessage$inboundSchema),
    num_messages: z.int(),
    originating_client: z.optional(z.string()),
    participants: z.optional(z.array(ChatParticipant$inboundSchema)),
    pinned: z.optional(z.boolean()),
    risk_findings_count: z.optional(z.int()),
    risk_segments: z.optional(z.array(RiskSegment$inboundSchema)),
    slack_channel_id: z.optional(z.string()),
    slack_channel_name: z.optional(z.string()),
    slack_team_id: z.optional(z.string()),
    source: z.optional(z.string()),
    summary: z.optional(z.string()),
    summary_generated_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    title: z.string(),
    total_cost: z.optional(z.number()),
    total_input_tokens: z.optional(z.int()),
    total_output_tokens: z.optional(z.int()),
    total_tokens: z.optional(z.int()),
    totals: z.optional(ChatTotals$inboundSchema),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    user_id: z.optional(z.string()),
    work_units: z.optional(z.number()),
    work_units_report: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "account_email": "accountEmail",
      "account_type": "accountType",
      "agent_id": "agentId",
      "agent_name": "agentName",
      "agent_usage": "agentUsage",
      "assistant_agent_id": "assistantAgentId",
      "assistant_id": "assistantId",
      "assistant_name": "assistantName",
      "content_parts": "contentParts",
      "created_at": "createdAt",
      "external_user_id": "externalUserId",
      "has_more_after": "hasMoreAfter",
      "has_more_before": "hasMoreBefore",
      "last_message_timestamp": "lastMessageTimestamp",
      "litellm_proxied": "litellmProxied",
      "match_segments": "matchSegments",
      "max_generation": "maxGeneration",
      "num_messages": "numMessages",
      "originating_client": "originatingClient",
      "risk_findings_count": "riskFindingsCount",
      "risk_segments": "riskSegments",
      "slack_channel_id": "slackChannelId",
      "slack_channel_name": "slackChannelName",
      "slack_team_id": "slackTeamId",
      "summary_generated_at": "summaryGeneratedAt",
      "total_cost": "totalCost",
      "total_input_tokens": "totalInputTokens",
      "total_output_tokens": "totalOutputTokens",
      "total_tokens": "totalTokens",
      "updated_at": "updatedAt",
      "user_id": "userId",
      "work_units": "workUnits",
      "work_units_report": "workUnitsReport",
    });
  }),
);

export function chatFromJSON(
  jsonString: string,
): SafeParseResult<Chat, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Chat$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Chat' from JSON`,
  );
}
